import useAppInfoStore from 'src/store/appInfo';
import GeneralUpload from 'src/components/general/GeneralUpload';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { Grid, Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { FormProvider, RHFSwitch, RHFTextField } from '../../components/hook-form';
import { SkeletonForm } from '../../components/skeleton';
import { generateSelectMenuItems } from 'src/utils';
import { GeneralSelectMenu } from 'src/components/general';
import { useQueryClient } from '@tanstack/react-query';
import { GAME_LIST } from 'src/constants/queryKeys';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import useFormsModalStore from 'src/store/forms';
import {
  networkCreateGame,
  networkUpdateGame,
  useFetchGameDesignModeList,
  useFetchGameModeList,
  useFetchGameTypeList,
  useFetchOrientationList,
} from 'src/network/games';

// ----------------------------------------------------------------------

export default function GameForm() {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const { appInfo } = useAppInfoStore();

  const [gameOwnerList, setGameOwnerList] = useState([]);
  const [selectedGameOwner, setSelectedGameOwner] = useState('');

  const [mainLanguageList, setMainLanguageList] = useState([]);
  const [selectedMainLanguage, setSelectedMainLanguage] = useState('');

  const [gameCategories, setGameCategories] = useState([]);
  const [selectedGameCategory, setSelectedGameCategory] = useState('');

  const [gameModeList, setGameModeList] = useState([]);
  const [selectedGameMode, setSelectedGameMode] = useState('');

  const [gameOrientationList, setGameOrientationList] = useState([]);
  const [selectedGameOrientation, setSelectedGameOrientation] = useState('');

  const [positionControllerList, setPositionControllerList] = useState([]);
  const [selectedPositionController, setSelectedPositionController] = useState('');

  const [gameTypeList, setGameTypeList] = useState([]);
  const [selectedGameType, setSelectedGameType] = useState('');

  const [gameDesignModeList, setGameDesignModeList] = useState([]);
  const [selectedGameDesignMode, setSelectedGameDesignMode] = useState('');

  const [parentalGuidanceList, setParentalGuidanceList] = useState([]);
  const [selectedParentalGuidance, setSelectedParentalGuidance] = useState('');

  const [filterItems] = useState({
    ID: -1,
  });

  const gameDetails = modal?.data;

  // -------------------------------------------------------

  const positionControllers = [
    { id: 1, title: 'بالا سمت راست' },
    { id: 2, title: 'پایین سمت راست' },
    { id: 3, title: 'بالا سمت چپ' },
    { id: 4, title: 'پایین سمت چپ' },
  ];

  // -------------------------------------------------------
  const defaultValues = getDefaultValues(gameDetails);
  const methods = useForm({
    resolver: yupResolver(schema), // todo
    defaultValues,
  });

  const {
    reset,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GameSize: +values.GameSize,
      GameAvrageRate: +values.GameAvrageRate,
      GameGameCategoryID: selectedGameCategory || +gameCategories?.[0]?.GameCategoryID,
      GameDesignMode: selectedGameDesignMode,
      GameOrientationID: selectedGameOrientation,
      PositionController: selectedPositionController,
      GameModeID: selectedGameMode,
      GameTypeID: selectedGameType,
      IsMobile: values?.IsMobile ? 1 : 0,
      IsWeb: values?.IsWeb ? 1 : 0,
      GameParentalGuidance: selectedParentalGuidance,
      GameMainLanguageID: selectedMainLanguage,
      GameGameOwnerID: selectedGameOwner,
    };

    try {
      const res = await networkCreateGame(PAYLOAD);
      if (res.data?.items?.ResultID !== -1) {
        enqueueSnackbar('بازی موردنظر با موفقیت ایجاد شد');
        if (event.nativeEvent.submitter.name === 'save') {
          closeModal();
        }
        reset();

        setSelectedGameOrientation(gameOrientationList?.[0]?.id);
        setSelectedGameDesignMode(gameDesignModeList?.[0]?.id);
        setSelectedGameMode(gameModeList?.[0]?.id);
        setSelectedGameType(gameTypeList?.[0]?.id);
        setSelectedGameCategory(gameCategories?.[0]?.id);
        setSelectedPositionController(positionControllerList?.[0]?.id);

        await queryClient.invalidateQueries({ queryKey: [GAME_LIST] });
      } else {
        enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { data: gameOrientationResult, loading: loading1 } = useFetchOrientationList(filterItems);
  const { data: gameDesignModeResult, loading: loading2 } = useFetchGameDesignModeList(filterItems);
  const { data: gameModeResult, loading: loading3 } = useFetchGameModeList(filterItems);
  const { data: gameTypeResult, loading: loading4 } = useFetchGameTypeList(filterItems);

  useEffect(() => {
    if (gameOrientationResult) {
      const gameOrientationItems = generateSelectMenuItems(gameOrientationResult?.data?.items, 'GameOrientationLable', 'GameOrientationID');
      setGameOrientationList(gameOrientationItems);
      setSelectedGameOrientation(gameOrientationItems?.[0]?.id);
    }
  }, [gameOrientationResult]);

  useEffect(() => {
    if (gameDesignModeResult) {
      const gameDesignModeItems = generateSelectMenuItems(gameDesignModeResult?.data?.items, 'GameDesignModeLable', 'GameDesignModeID');
      setGameDesignModeList(gameDesignModeItems);
      setSelectedGameDesignMode(gameDesignModeItems?.[0]?.id);
    }
  }, [gameDesignModeResult]);

  useEffect(() => {
    if (gameModeResult) {
      const gameModeItems = generateSelectMenuItems(gameModeResult?.data?.items, 'GameModeLable', 'GameModeID');
      setGameModeList(gameModeItems);
      setSelectedGameMode(gameModeItems?.[0]?.id);
    }
  }, [gameModeResult]);

  useEffect(() => {
    if (gameTypeResult) {
      const gameTypeItems = generateSelectMenuItems(gameTypeResult?.data?.items, 'GameTypeLable', 'GameTypeID');
      setGameTypeList(gameTypeItems);
      setSelectedGameType(gameTypeItems?.[0]?.id);
    }
  }, [gameTypeResult]);

  useEffect(() => {
    if (appInfo) {
      const gameCategories = generateSelectMenuItems(appInfo?.CategoryGame, 'GameCategoryLable', 'GameCategoryID');

      const parentalGuidances = generateSelectMenuItems(appInfo.ParentalGuidance, 'ParentalGuidanceName', 'ParentalGuidanceID');

      const gameOwners = generateSelectMenuItems(appInfo.GameOwner, 'GameOwnerLable', 'GameOwnerID');

      const mainLanguages = generateSelectMenuItems(appInfo.MainLanguage, 'MainLanguageTitle', 'MainLanguageID');

      setGameCategories(gameCategories);
      setSelectedGameCategory(gameCategories?.[0]?.id);

      setPositionControllerList(positionControllers);
      setSelectedPositionController(positionControllers?.[0]?.id);

      setParentalGuidanceList(parentalGuidances);
      setSelectedParentalGuidance(parentalGuidances?.[0]?.id);

      setGameOwnerList(gameOwners);
      setSelectedGameOwner(gameOwners?.[0]?.id);

      setMainLanguageList(mainLanguages);
      setSelectedMainLanguage(mainLanguages?.[0]?.id);
    }
  }, [appInfo]);

  const isLoading = loading1 || loading2 || loading3 || loading4;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={16} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <GeneralSelectMenu
                    label="تامین کننده بازی"
                    name="GameGameOwnerID"
                    options={gameOwnerList}
                    value={selectedGameOwner}
                    onChangeValue={setSelectedGameOwner}
                  />

                  <GeneralSelectMenu
                    label="زبان بازی"
                    name="GameGameOwnerID"
                    options={mainLanguageList}
                    value={selectedMainLanguage}
                    onChangeValue={setSelectedMainLanguage}
                  />

                  <RHFTextField name="GameName" type="text" label="عنوان فارسی" />
                  <RHFTextField name="GameNameEn" type="text" label="عنوان انگلیسی" />
                  <RHFTextField name="GameURL" type="text" label="لینک فارسی" dir="ltr" />
                  <RHFTextField name="GameUrlEn" type="text" label="لینک انگلیسی" dir="ltr" />
                  <GeneralSelectMenu
                    label="ژانر بازی"
                    name="GameCategory"
                    options={gameCategories}
                    value={selectedGameCategory}
                    onChangeValue={setSelectedGameCategory}
                  />
                  <GeneralSelectMenu
                    label="سبک بازی"
                    name="GameModeID"
                    options={gameModeList}
                    value={selectedGameMode}
                    onChangeValue={setSelectedGameMode}
                  />
                  <GeneralSelectMenu
                    label="جهت اجرای بازی"
                    name="GameOrientationID"
                    options={gameOrientationList}
                    value={selectedGameOrientation}
                    onChangeValue={setSelectedGameOrientation}
                  />
                  <GeneralSelectMenu
                    label="موقعیت نمایش دکمه ی اطلاعات"
                    name="PositionController"
                    options={positionControllerList}
                    value={selectedPositionController}
                    onChangeValue={setSelectedPositionController}
                  />
                  <GeneralSelectMenu
                    label="نوع بازی"
                    name="GameTypeID"
                    options={gameTypeList}
                    value={selectedGameType}
                    onChangeValue={setSelectedGameType}
                  />
                  <GeneralSelectMenu
                    label="پلتفرم توسعه"
                    name="GameDesignMode"
                    options={gameDesignModeList}
                    value={selectedGameDesignMode}
                    onChangeValue={setSelectedGameDesignMode}
                  />
                  <GeneralSelectMenu
                    label="رده سنی"
                    name="game-parentalguidance"
                    options={parentalGuidanceList}
                    value={selectedParentalGuidance}
                    onChangeValue={setSelectedParentalGuidance}
                  />
                  <RHFTextField name="GameSize" type="text" label="حجم بازی" />
                  <RHFTextField name="GameVersion" type="text" label="ورژن بازی" />
                  <RHFTextField name="GameAvrageRate" type="text" label="میانگین امتیاز بازی" />
                  <RHFTextField name="GameTitleButton" type="text" label="عنوان دکمه بازی" />
                  <br />
                  <GeneralUpload
                    label="لوگوی بازی"
                    id="game_logo"
                    name="GameLogo"
                    afterChangeAction={(res) => {
                      setValue('GameLogo', res);
                      trigger('GameLogo');
                    }}
                  />
                  <GeneralUpload
                    label="بگ گراند بازی"
                    id="game_backdrop"
                    name="GameBackDrop"
                    afterChangeAction={(res) => {
                      setValue('GameBackDrop', res);
                      trigger('GameBackDrop');
                    }}
                  />
                  <RHFTextField name="GameDesc" multiline rows={6} label="توضیحات فارسی" />
                  <RHFTextField name="GameDescEn" multiline rows={6} label="توضیحات انگلیسی" />
                  <RHFTextField name="GameSummery" multiline rows={6} label="خلاصه ای از بازی به فارسی" />
                  <RHFTextField name="GameSummeryEn" multiline rows={6} label="خلاصه ای از بازی به انگلیسی" />
                  <RHFSwitch name="IsMobile" labelPlacement="end" label="قابلیت اجرا در موبایل" />
                  <RHFSwitch name="IsWeb" labelPlacement="end" label="قابلیت اجرا در وب" />
                </Box>

                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    ذخیره و ایجاد بعدی
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
